// CommonJS on purpose: jest loads this file in packages that have no babel
// config (dashboard-commons), so it must execute without any transform.
const vfd = require('vue-facing-decorator');

// SHIM: old vue-property-decorator's @Prop() allows a bare constructor shorthand
// (`@Prop(String)`) as sugar for `{ type: String }`. vue-facing-decorator's Prop only
// accepts a PropsConfig object (or nothing) — a bare constructor fails validation
// ("Value of type 'StringConstructor' has no properties in common with type
// 'PropsConfig'"). ~15 files / ~28 call sites use the shorthand, so this thin
// normalization wrapper earns its keep.
function isPropCtorShorthand(value) {
    return value === String || value === Number || value === Boolean
        || value === Object || value === Array || value === Function || value === Symbol
        || Array.isArray(value);
}
function Prop(option) {
    if (arguments.length === 0) {
        return vfd.Prop();
    }
    if (isPropCtorShorthand(option)) {
        return vfd.Prop({ type: option });
    }
    return vfd.Prop(option);
}

// SHIM (real bug, not just typing): old vue-property-decorator's @Inject(key) takes the
// injection key POSITIONALLY (string|symbol|InjectionKey). vue-facing-decorator's Inject
// expects an `{ from, default }` config object — passing a bare key straight through
// silently loses it: `Object.assign({}, someSymbol)` (what its decorator does
// internally) evaluates to `{}` because a boxed Symbol has no own enumerable
// properties. That makes `build()` fall back to injecting under the class *property
// name* instead of the intended key — a real functional regression, confirmed against
// CriterionInputs/AbstractCriterionInput.ts's `@Inject(setSchemaVisibleInjectKey)` /
// `@InjectReactive(formValueInjectKey)` (both keys are bare `Symbol()`s). Provide's
// vue-facing-decorator signature already takes the key positionally (matches the old
// convention), so Provide/ProvideReactive need no runtime shim, only a widened .d.ts
// type (symbols already work fine as object keys at runtime — see index.d.ts).
function Inject(option) {
    if (typeof option === 'string' || typeof option === 'symbol') {
        return vfd.Inject({ from: option });
    }
    return vfd.Inject(option);
}

// PRODUCT-26110 Phase 2B2 Task 6: root-causes a "TypeError: Class constructor <X>
// cannot be invoked without 'new'" crash that hits EVERY vue-property-decorator class
// component (e.g. `class MenuLink extends Vue {}`) as soon as jest tries to mount it.
//
// Mechanism: `vfd.Vue` (aliased `Base` in vue-facing-decorator's own source) is a
// genuine ES2015+ `class`. `@vue/vue3-jest`'s TypeScript transformer forces an ES5
// compile target for every `.vue` SFC's `<script lang="ts">` block (unrelated to this
// fork; a pre-existing vue3-jest quirk). TypeScript's ES5 downlevel of
// `class MenuLink extends Vue {}` emits a plain function wrapped in an IIFE that
// invokes the parent via `_super.apply(this, arguments)` -- and native `class`
// constructors reject being invoked via `.apply()`/`.call()`/without `new`, throwing
// exactly that TypeError the instant `new MenuLink(...)` runs (vue-facing-decorator's
// own `data()` builder does this on every mount). Under old Vue 2 + vue-class-
// component, this never happened: the `Vue` global constructor was itself an
// ES5-style plain function, so ES5-downleveled subclasses could always call it via
// `.apply()`.
//
// Fix: export a thin wrapper function instead of the bare `class`, tolerant of BOTH
// invocation styles:
//   - Called via `new` (including the implicit `super()` call a REAL ES2015+
//     `class X extends Vue {}` makes -- `new.target` propagates correctly through
//     native derived-class construction even when the base is an ordinary function,
//     not another `class`): delegates to `Reflect.construct(Base, args, new.target)`,
//     preserving `Base`'s exact prototype/identity for real subclass hierarchies.
//   - Called via `.apply()`/`.call()` without `new` (the ES5-downlevel case above):
//     `this` already exists (the ES5-downleveled subclass instance, prototype-linked
//     via TS's `__extends` helper) -- runs `Base`'s constructor the correct way
//     (`new Base(...)`, since `Base` itself still requires `new`) and copies its own
//     enumerable properties onto `this` via `Object.assign`, rather than duplicating
//     `Base`'s constructor logic here (which would drift if vue-facing-decorator ever
//     changes it).
// `Vue.prototype` is set to the EXACT SAME object as `Base.prototype` (not a copy) so
// `instanceof Base`/prototype-chain checks anywhere in vue-facing-decorator's internals
// keep working unchanged.
function Vue(optionBuilder, vueInstance) {
    if (new.target) {
        return Reflect.construct(vfd.Vue, [ optionBuilder, vueInstance ], new.target);
    }
    // PRODUCT-26110 Phase 2B2 Task 6b fix-up: the ES5-downlevel `.apply()` path used to
    // `Object.assign(this, new vfd.Vue(...))` -- copying `new vfd.Vue(...)`'s OWN
    // ENUMERABLE properties onto the ES5-downleveled subclass's pre-allocated `this`.
    // That silently drops anything vfd exposes as a non-enumerable accessor rather than a
    // plain own-enumerable data property -- confirmed as a real bug (not hypothetical) via
    // a multi-level `Mixins(...)`-based subclass whose OWN class-field initializer read a
    // `@Prop`-declared value (`this.form.getValue(...)`) synchronously during construction
    // and got `undefined`, because prop access is resolved through Vue's component-
    // instance context, not a plain copyable data property. Fixed by RETURNING the
    // properly-constructed instance instead of mutating `this` in place: TypeScript's ES5
    // downlevel of `class X extends Vue { field = ...; constructor(...a){ super(...a); ...
    // } }` emits `var _this = _super.apply(this, arguments) || this; _this.field = ...;`
    // -- i.e. it already captures and uses whatever truthy object `_super.apply(...)`
    // returns as `_this` for every subsequent field initializer and the constructor's own
    // body, precisely because plain functions (unlike native classes) are allowed to
    // override their constructed object by returning one. Returning a genuine
    // `Reflect.construct(...)`'d instance (not a copy) preserves every accessor/proxy vfd
    // sets up, not just enumerable own properties.
    return Reflect.construct(vfd.Vue, [ optionBuilder, vueInstance ], vfd.Vue);
}
Vue.prototype = vfd.Vue.prototype;
Object.setPrototypeOf(Vue, vfd.Vue);

// PRODUCT-26110 Phase 2B2 Task 6b: the identical ES5-downlevel root cause as the `Vue`
// wrapper above (see its comment), but for `Mixins(...)`'s return value.
// `vfd.mixins(...conses)` builds `class MixinsClass extends <vue-facing-decorator's own
// internal Vue> {}` -- a SECOND, independent genuine ES2015+ class. Any repo class doing
// `class X extends Mixins(Y) {}` gets ES5-downleveled by vue3-jest the same way `class X
// extends Vue {}` does, and `_super.apply(this, arguments)` throws the same "Class
// constructor MixinsClass cannot be invoked without 'new'" TypeError the instant ANY such
// component mounts (measured: 64 of the 518 tprm-portal test failures in the pre-codemod
// baseline traced to this one bucket -- the single largest failure class, bigger than the
// findComponent stub-matching issue). Confirmed via the repo's own abstract-base
// convention (`AbstractCriterionInput.ts`/`AbstractJsonSchemaForm.ts`:
// `const XMixin = Mixins(X) as typeof X; export default XMixin;`), which mounts the
// `Mixins(...)` result directly as a component in some call sites -- so the wrapper must
// preserve `MixinsClass`'s own STATIC `__vccOpts` stamp (vue-facing-decorator's `build()`
// sets it directly on the class object itself, not its prototype), not just the instance
// prototype. Delegating `Wrapped`'s own [[Prototype]] to `MixinsClass` (mirroring the
// `Vue` wrapper's `Object.setPrototypeOf(Vue, vfd.Vue)` line above) makes static lookups
// like `__vccOpts` fall through correctly without hand-copying properties.
function wrapClassForEs5Downlevel(RealClass) {
    function Wrapped(...args) {
        if (new.target) {
            return Reflect.construct(RealClass, args, new.target);
        }
        // See the `Vue` wrapper's matching comment above: return the real constructed
        // instance (not an Object.assign'd copy of it) so the ES5-downleveled subclass's
        // `_this = _super.apply(this, arguments) || this` capture pattern picks it up,
        // preserving accessor-based prop access for the subclass's own field initializers.
        return Reflect.construct(RealClass, args, RealClass);
    }
    Wrapped.prototype = RealClass.prototype;
    Object.setPrototypeOf(Wrapped, RealClass);
    // A function's own `.name` is an own (if non-enumerable) property, so it is NOT
    // covered by the `Object.setPrototypeOf` static-delegation trick above -- `Wrapped`
    // would otherwise report its own literal name ("Wrapped") instead of the real
    // component's class name. This matters beyond cosmetics: `@vue/test-utils`'s
    // `findComponent(SomeClassComponent)` falls back to comparing `selector.name` against
    // the rendered vnode's component name when reference-identity matching fails (a
    // routine occurrence under `@vue/compat`, which clones/wraps vnode component types
    // during its legacy-conversion pass) -- every `@Component`-decorated class silently
    // losing its name to `wrapClassForEs5Downlevel` would break that fallback for EVERY
    // class component, not just the ones already affected by the identity mismatch.
    Object.defineProperty(Wrapped, 'name', { value: RealClass.name, configurable: true });
    return Wrapped;
}
function Mixins(...conses) {
    return wrapClassForEs5Downlevel(vfd.mixins(...conses));
}

// PRODUCT-26110 Phase 2B2 Task 6b: generalizes the same ES5-downlevel fix to every
// `@Component`-decorated class, not just this fork's own `Vue`/`Mixins` exports. Repo
// classes like `AbstractJsonSchemaFormElement`/`AbstractCriterionInput` (plain `.ts`
// files, own abstract-base classes -- NOT `Vue` or `Mixins(...)` directly) are compiled
// by plain ts-jest, which does NOT force an ES5 target (only `@vue/vue3-jest` forces ES5,
// and only for `.vue` SFCs' own `<script>` blocks) -- so these bases stay genuine
// ES2015+ classes. When a `.vue` SFC subclasses one of them (`class Input extends
// AbstractJsonSchemaFormElement {}`), THAT subclass gets ES5-downleveled, and
// `_super.apply(this, arguments)` throws the exact same "Class constructor
// <Base> cannot be invoked without 'new'" TypeError against `AbstractJsonSchemaFormElement`
// itself -- confirmed by this exact message appearing in the tprm-portal jest baseline
// even after the dedicated `Vue`/`Mixins` wrappers above landed. Wrapping `@Component`
// itself (rather than patching every abstract-base app-code file individually, which
// would be app-code scope creep well beyond this fork) fixes every case transitively:
// `vfd.Component`'s own decorator function explicitly `return`s the (mutated-in-place)
// class -- confirmed by reading `vue-facing-decorator/dist/component.js`'s `ComponentBase`
// (`build(cons, option); return cons;`), which is why TS's legacy decorator runtime
// accepts our own additional wrapping as the replacement binding, both for bare
// `@Component` usage (TS passes the class straight to `Component`, matching the
// `typeof arg === 'function'` branch below) and factory `@Component({...})` usage
// (`Component(options)` must first return a decorator function, which TS then applies
// to the class).
function Component(arg) {
    if (typeof arg === 'function') {
        const cons = vfd.Component(arg);
        return wrapClassForEs5Downlevel(cons || arg);
    }
    const decorator = vfd.Component(arg);
    return function classDecorator(target) {
        const cons = decorator(target);
        return wrapClassForEs5Downlevel(cons || target);
    };
}

module.exports = {
    Component,
    // vue-facing-decorator exports `mixins` (lowercase, and only as a named function,
    // not a decorator) — re-exported here under the historical `Mixins` name so the
    // ~6 call sites (all single-arg, TS-generic-erasure usage) don't need to change.
    // PRODUCT-26110 Phase 2B2 Task 6b: wrapped via `wrapClassForEs5Downlevel` (not the
    // bare `vfd.mixins`) -- see that function's comment for the ES5-downlevel root cause.
    Mixins,
    Emit: vfd.Emit,
    // vue-facing-decorator has no InjectReactive/ProvideReactive. Its plain Inject/
    // Provide are ALREADY reactive (Provide's builder wraps the value in Vue's
    // `computed()`, and Vue's own `resolveInjections()` auto-unwraps refs it receives
    // via the Options API `inject` option) — i.e. they already behave like the OLD
    // *Reactive variants, not the old plain Inject/Provide. This is a same-behavior
    // rename, not new shim logic.
    Inject,
    InjectReactive: Inject,
    Provide: vfd.Provide,
    ProvideReactive: vfd.Provide,
    // `Model`/`VModel` are literally the same export in vue-facing-decorator (both
    // alias the same `option/vmodel` decorator).
    Model: vfd.Model,
    // ModelSync/PropSync: no vue-facing-decorator equivalent, and zero call sites exist
    // anywhere in the repo today. Exporting `undefined` would defer any future misuse to
    // a confusing "X is not a function" at whatever call site tries to use it as a
    // decorator — throwing here instead fails immediately and points straight at the fix.
    get ModelSync() {
        throw new Error('ModelSync has no vue-facing-decorator equivalent (PRODUCT-26110); use @Model/@VModel or a computed');
    },
    get PropSync() {
        throw new Error('PropSync has no vue-facing-decorator equivalent (PRODUCT-26110); use @Prop + computed or @VModel');
    },
    Prop,
    Ref: vfd.Ref,
    VModel: vfd.VModel,
    Watch: vfd.Watch,
    // PRODUCT-26110 Phase 2B2 Task 4: the transitional compat-statics graft (Vue.extend/
    // use/component/directive/mixin/nextTick/set/delete/observable/filter/version copied
    // onto vfd.Vue) has been removed now that Task 4's codemod migrated every
    // fork-imported `Vue.extend(...)` call site to `defineComponent(...)`, and a grep
    // audit confirmed zero remaining fork-Vue global-API callers repo-wide. Bare `import
    // Vue from 'vue'` callers still reach @vue/compat's own global API directly (that's
    // unaffected by this fork) -- those are burned down in Phase 2B3, not here.
    // PRODUCT-26110 Phase 2B2 Task 6: exports the `Vue` wrapper defined above (NOT bare
    // `vfd.Vue`) -- see that comment for the ES5-downlevel root-cause it fixes.
    Vue,
    // Exported for completeness / future use. Not required at any existing call site
    // today: Component's `__vccOpts` stamp is auto-unwrapped by Vue 3's own
    // isClassComponent()/normalizeClassComponent() and by @vue/test-utils@2's identical
    // check, so "export default class X extends Vue {}" keeps working unchanged without
    // ever calling toNative().
    toNative: vfd.toNative,
};
